import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Loader2, FolderKanban } from 'lucide-react';
import { MultiColumnStatement } from '../reports/MultiColumnStatement';
import { normalizePnl } from '../../utils/reportNormalize';

// Columns = every project (+ Unassigned + Total), same detailed P&L template as the single report.
export const projectColumns = (data) => {
  const cols = [...(data?.columns || []), data?.unassigned].filter(Boolean).map((c) => ({
    key: c.project_id,
    label: `${c.project_name}${c.status === 'archived' ? ' (archived)' : ''}`,
    data: normalizePnl(c.full || {}),
  }));
  return cols;
};

export const ProjectColumnsPnl = ({ startDate, endDate, displayCurrency, formatAmount, showGlCodes, openDrilldown, cogsLabel, netLabel, onData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const ccy = displayCurrency ? `&display_currency=${displayCurrency}` : '';
    axios.get(`${API}/unified-reports/profit-loss/by-project?start_date=${startDate}&end_date=${endDate}${ccy}`)
      .then((r) => { if (!cancelled) { setData(r.data); onData && onData(r.data); } })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Failed to load project columns'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, displayCurrency]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="by-project-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-red-600 py-8 text-center" data-testid="by-project-error">{error}</p>;
  }
  if (!data) return null;

  return (
    <MultiColumnStatement
      reportType="pnl"
      columns={projectColumns(data)}
      formatAmount={formatAmount}
      showGlCodes={showGlCodes}
      openDrilldown={openDrilldown}
      cogsLabel={cogsLabel}
      netLabel={netLabel}
      icon={FolderKanban}
      title="Profit & Loss · Columns by Project"
      description="One column per project plus Unassigned, with the full account breakdown. Columns always add up to the Total P&L for the period."
      testId="by-project-pnl"
    />
  );
};
